import React, { useState, useMemo, useEffect, useRef } from "react";
import JSConfetti from "js-confetti";
import uuid from "utils/uuid";

const shuffle = (cards) => {
  const doubled = [...cards, ...cards].map((_, i) => ({
    key: uuid(),              // unique ID for each card instance
    index: i % cards.length,  // reference to the base card
  }));
  return doubled.sort(() => Math.random() - 0.5);
}

export default function PexesoGamePreview({ assets, back = "/static/img/family-flip-back.png", perRow = 8, primaryColor = "#27326C" }) {
  const confettiRef = useRef();

  // Initialize confetti instance once after mount
  useEffect(() => {
    confettiRef.current = new JSConfetti();
  }, []);

  // Prepare cards from assets
  const cards = useMemo(() => {
    const items = assets.map((item) => {
      if (typeof item === "string") {
        return (
          <img
            key={item}
            src={item}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            alt=""
          />
        );
      }
      return item;
    });

    // Special case: if assets are PDF preview pages (every 4th one is unique)
    // last two pages are instructions
    if (
      (assets.length - 2) % 4 === 0 &&
      assets.every(
        (item) => typeof item === "string" && item.includes("pages-00")
      )
    ) {
      // first remove last two pages and then only every 4th
      return items.slice(0, -2).filter((_, i) => i % 4 === 0);
    }

    return items;
  }, [assets]);

  const [flipped, setFlipped] = useState([]); // currently flipped card keys
  const [matched, setMatched] = useState([]); // matched card indexes (by reference to `cards`)

  // Build the deck: duplicate each card and shuffle
  const [shuffled, setShuffled] = useState([])
  useEffect(() => {
    setShuffled(shuffle(cards))
  }, [])

  // Handle a card flip
  const handleFlip = (card) => {
    // Ignore if already two cards flipped, card is already flipped, or already matched
    if (
      flipped.length === 2 ||
      flipped.includes(card.key) ||
      matched.includes(card.index)
    ) {
      return;
    }

    const newFlipped = [...flipped, card.key];
    setFlipped(newFlipped);

    // When two cards are flipped, check for match
    if (newFlipped.length === 2) {
      const [first, second] = newFlipped.map((k) =>
        shuffled.find((c) => c.key === k)
      );

      if (first.index === second.index) {
        // Cards match → mark as matched and fire confetti
        setMatched((m) => [...m, first.index]);
        confettiRef.current.addConfetti();
        setFlipped([]);
      } else {
        // Not a match → flip them back after 1s
        setTimeout(() => setFlipped([]), 1000);
      }
    }
  };

  // Reset game
  const handleStartOver = () => {
    setFlipped([]);
    setMatched([]);
    setTimeout(() => setShuffled(shuffle(cards)), 400); // trigger reshuffle
  };

  const cardSize = `${100 / perRow}%`;
  const isGameOver = matched.length === cards.length && cards.length > 0;

  return (
    <div style={{ position: "relative" }}>
      {/* Game board */}
      <div
        className="d-flex flex-wrap justify-content-center"
        style={{
          margin: "0 auto",
          opacity: isGameOver ? 0.25 : 1,
          transition: "opacity 0.35s",
        }}
      >
        {shuffled.map((card) => {
          const isFlipped =
            flipped.includes(card.key) || matched.includes(card.index);

          return (
            <div
              key={card.key}
              onClick={() => handleFlip(card)}
              style={{
                width: cardSize,
                aspectRatio: "1 / 1",
                padding: "5px",
                perspective: "1000px",
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  transformStyle: "preserve-3d",
                  transition: "transform 0.6s",
                  transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                  cursor: "pointer",
                }}
              >
                {/* Back side of card */}
                <div
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    backfaceVisibility: "hidden",
                    background: `url('${back}') center/cover no-repeat`,
                    borderRadius: "8px",
                    boxShadow: "0 0 5px rgba(0,0,0,0.3)",
                    overflow: "hidden",
                  }}
                ></div>

                {/* Front side of card */}
                <div
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    backfaceVisibility: "hidden",
                    transform: "rotateY(180deg)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "2rem",
                    borderRadius: "8px",
                    overflow: "hidden",
                    background: "#fff",
                    boxShadow: "0 0 5px rgba(0,0,0,0.3)",
                  }}
                >
                  {cards[card.index]}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Game over overlay */}
      {isGameOver && (
        <div
          role="button"
          onClick={handleStartOver}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: primaryColor,
            color: "#fff",
            padding: "18px 36px",
            borderRadius: "12px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.35)",
            fontSize: "1.6rem",
            cursor: "pointer",
            textAlign: "center",
            zIndex: 20,
            userSelect: "none",
          }}
        >
          Start Over
        </div>
      )}
    </div>
  );
}
